import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ImagePlus, LifeBuoy, Loader2, Mail, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  COMPANY_ADDRESS_LINES, COMPANY_NAME, LEGAL_NAME, SUPPORT_EMAIL,
} from "@/constants/company";

const AREAS = [
  { id: "shop", label: "Shop" },
  { id: "seller", label: "Seller" },
  { id: "developer", label: "Developer" },
  { id: "other", label: "Something else" },
];

const field = "mt-1 w-full px-3 py-2 border border-aura-border rounded-lg bg-transparent text-sm";

function Shot({ ticketId, file }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file?.id) return undefined;
    let objectUrl = "";
    let alive = true;
    api.get(`/support/tickets/${ticketId}/files/${file.id}`, { responseType: "blob" })
      .then(({ data }) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketId, file?.id]);
  if (!file?.id) {
    return <span className="text-xs text-aura-muted">{file?.filename || "Screenshot"}</span>;
  }
  if (!url) return <span className="text-xs text-aura-muted">{file.filename}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={file.filename || "Screenshot"} className="max-h-48 rounded border border-aura-border" />
    </a>
  );
}

function who(event) {
  if (event.actor_role === "support") return "Shoptosa support";
  return event.actor_name || event.actor_email || "You";
}

export default function Contact() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("ticket") || "";
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [portal, setPortal] = useState("shop");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [composing, setComposing] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [replying, setReplying] = useState(false);

  const loadList = useCallback(async () => {
    if (!user) return;
    const { data } = await api.get("/support/tickets");
    setTickets(data || []);
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    loadList().catch((err) => {
      if (alive) toast.error(apiError(err));
    });
    return () => { alive = false; };
  }, [user, loadList]);

  useEffect(() => {
    if (!user || !selectedId) {
      setTicket(null);
      return undefined;
    }
    let alive = true;
    api.get(`/support/tickets/${selectedId}`)
      .then(({ data }) => { if (alive) setTicket(data); })
      .catch((err) => { if (alive) toast.error(apiError(err)); });
    return () => { alive = false; };
  }, [user, selectedId]);

  const addFiles = (list, current, setter) => {
    const next = [...current];
    for (const file of list) {
      if (next.length >= 3) break;
      if (!file.type.startsWith("image/")) {
        toast.error("Attach a PNG, JPG, WEBP, or GIF screenshot.");
        continue;
      }
      if (file.size > 4 * 1024 * 1024) {
        toast.error("Each screenshot must be 4 MB or smaller.");
        continue;
      }
      next.push(file);
    }
    setter(next);
  };

  const openTicket = (id) => {
    setComposing(false);
    setParams(id ? { ticket: id } : {});
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = new FormData();
      body.append("subject", topic);
      body.append("message", message);
      body.append("portal", portal);
      files.forEach((file) => body.append("files", file));
      const { data } = await api.post("/support/tickets", body);
      setTopic("");
      setMessage("");
      setFiles([]);
      setComposing(false);
      toast.success(`Ticket ${data.id} is open`);
      await loadList();
      openTicket(data.id);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!ticket) return;
    setReplying(true);
    try {
      const body = new FormData();
      body.append("message", reply);
      replyFiles.forEach((file) => body.append("files", file));
      const { data } = await api.post(`/support/tickets/${ticket.id}/replies`, body);
      setTicket(data);
      setReply("");
      setReplyFiles([]);
      await loadList();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReplying(false);
    }
  };

  const setStatus = async (action) => {
    if (!ticket) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/support/tickets/${ticket.id}/${action}`);
      setTicket(data);
      await loadList();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReplying(false);
    }
  };

  const staff = user?.role === "admin";

  return (
    <div className="max-w-[860px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="support-page">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Support</div>
      <h1 className="font-serif text-4xl tracking-tight mb-4">Support tickets</h1>
      <p className="text-base text-aura-muted leading-relaxed max-w-2xl mb-10">
        Every open, reply, and close is saved on the ticket. Write the reply here so both sides see it.
        A reply typed in your email app is not added to the ticket.
      </p>

      {user ? (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium" data-testid="support-ticket-heading">
              {staff ? "All tickets" : "Your tickets"}
            </div>
            <button
              type="button"
              className="text-sm link-underline"
              onClick={() => { setComposing(true); setParams({}); }}
              data-testid="support-new-ticket"
            >
              New ticket
            </button>
          </div>
          <ul className="border border-aura-border rounded-lg divide-y divide-aura-border mb-6" data-testid="support-ticket-list">
            {tickets.length === 0 && (
              <li className="p-4 text-sm text-aura-muted">No tickets yet.</li>
            )}
            {tickets.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => openTicket(row.id)}
                  className={`w-full text-left px-4 py-3 ${selectedId === row.id ? "bg-aura-surface" : ""}`}
                  data-testid={`support-ticket-${row.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-sm truncate">{row.topic}</span>
                    <span className="text-[11px] uppercase tracking-luxe text-aura-muted shrink-0">{row.status}</span>
                  </div>
                  <div className="text-xs text-aura-muted mt-1">
                    {row.id}{staff ? ` · ${row.email}` : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {composing && (
            <form onSubmit={submit} className="border border-aura-border rounded-lg p-6 mb-6 space-y-4" data-testid="support-ticket-form">
              <label className="block text-xs text-aura-muted">
                Account email
                <input value={user.email || ""} readOnly className={`${field} text-aura-ink`} data-testid="support-account-email" />
              </label>
              <label className="block text-xs text-aura-muted">
                Where this is about
                <select value={portal} onChange={(e) => setPortal(e.target.value)} className={field} data-testid="support-portal">
                  {AREAS.map((area) => <option key={area.id} value={area.id}>{area.label}</option>)}
                </select>
              </label>
              <label className="block text-xs text-aura-muted">
                Subject
                <input
                  required
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  maxLength={120}
                  placeholder="What do you need help with?"
                  className={field}
                  data-testid="support-subject"
                />
              </label>
              <label className="block text-xs text-aura-muted">
                Message
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="What happened, and what did you expect?"
                  className={field}
                  data-testid="support-message"
                />
              </label>
              <FilePicker
                files={files}
                onAdd={(list) => addFiles(list, files, setFiles)}
                onRemove={(index) => setFiles(files.filter((_, i) => i !== index))}
                testId="support-files"
              />
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 px-5 py-2.5 btn-accent text-sm rounded-full disabled:opacity-60"
                data-testid="support-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LifeBuoy className="w-4 h-4" />}
                Send ticket
              </button>
            </form>
          )}

          {ticket && (
            <div className="border border-aura-border rounded-lg p-6" data-testid="support-thread">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="font-medium">{ticket.topic}</div>
                  <div className="text-xs text-aura-muted mt-1">
                    {ticket.id} · {ticket.email} · {ticket.status}
                  </div>
                </div>
                {ticket.status === "closed" ? (
                  <button
                    type="button"
                    disabled={replying}
                    onClick={() => setStatus("reopen")}
                    className="px-4 py-2 border border-aura-border text-sm rounded-full disabled:opacity-60"
                    data-testid="support-reopen"
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={replying}
                    onClick={() => setStatus("close")}
                    className="px-4 py-2 border border-aura-border text-sm rounded-full disabled:opacity-60"
                    data-testid="support-close"
                  >
                    Close ticket
                  </button>
                )}
              </div>
              <ol className="space-y-4 mb-6">
                {(ticket.events || []).map((event) => (
                  <li key={event.id || `${event.kind}-${event.at}`} className="text-sm">
                    <div className="text-xs text-aura-muted mb-1">
                      {who(event)} · {event.kind}
                    </div>
                    <p className="leading-relaxed whitespace-pre-wrap">{event.body}</p>
                    {(event.files || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {event.files.map((file) => (
                          <Shot key={file.id || file.filename} ticketId={ticket.id} file={file} />
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
              {ticket.status === "open" && (
                <form onSubmit={sendReply} className="space-y-3" data-testid="support-reply">
                  <p className="text-xs text-aura-muted">
                    This reply is saved on the ticket and emailed. A reply in your inbox is not added here.
                  </p>
                  <textarea
                    required
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    maxLength={5000}
                    placeholder="Write a reply"
                    className={field}
                    data-testid="support-reply-message"
                  />
                  <FilePicker
                    files={replyFiles}
                    onAdd={(list) => addFiles(list, replyFiles, setReplyFiles)}
                    onRemove={(index) => setReplyFiles(replyFiles.filter((_, i) => i !== index))}
                    testId="support-reply-files"
                  />
                  <button
                    type="submit"
                    disabled={replying}
                    className="inline-flex items-center gap-2 px-5 py-2.5 btn-accent text-sm rounded-full disabled:opacity-60"
                    data-testid="support-reply-submit"
                  >
                    {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Send reply
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="border border-aura-border rounded-lg p-6 mb-8" data-testid="support-sign-in">
          <LifeBuoy className="w-5 h-5 text-aura-accent mb-3" />
          <div className="font-medium mb-1">Sign in to open a ticket</div>
          <p className="text-sm text-aura-muted leading-relaxed mb-4">
            A ticket is sent from the email on your account, so we know who wrote it.
            You can attach a screenshot. Open tickets, replies, and closes stay on this page.
          </p>
          <Link to="/auth?mode=login" className="inline-flex items-center gap-2 px-5 py-2.5 btn-accent text-sm rounded-full">
            Sign in
          </Link>
        </div>
      )}

      <div className="border border-aura-border rounded-lg p-6 mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-testid="support-other">
        <div className="flex items-start gap-4 min-w-0">
          <Mail className="w-5 h-5 text-aura-accent mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium mb-1">Any other question</div>
            <p className="text-sm text-aura-muted leading-relaxed">
              Partnerships, privacy requests, and anything that is not a ticket. Mail is delivered to the support team.
            </p>
          </div>
        </div>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-aura-border text-sm rounded-full shrink-0 self-start sm:self-center"
        >
          <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
        </a>
      </div>

      <div className="border border-aura-border rounded-lg p-6 flex items-center gap-4">
        <MapPin className="w-5 h-5 text-aura-accent shrink-0" />
        <div>
          <div className="font-medium mb-1">{COMPANY_NAME}</div>
          <p className="text-sm text-aura-muted leading-relaxed">{COMPANY_NAME} is part of {LEGAL_NAME}.</p>
          {COMPANY_ADDRESS_LINES.map((line) => (
            <p key={line} className="text-sm text-aura-muted leading-relaxed">{line}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

function FilePicker({ files, onAdd, onRemove, testId }) {
  return (
    <div>
      <div className="text-xs text-aura-muted mb-2">Screenshot (optional, up to 3)</div>
      <label className="inline-flex items-center gap-2 px-4 py-2 border border-aura-border rounded-full text-sm cursor-pointer">
        <ImagePlus className="w-4 h-4" /> Add image
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          data-testid={testId}
          onChange={(e) => {
            onAdd(e.target.files || []);
            e.target.value = "";
          }}
        />
      </label>
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-2 text-sm">
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => onRemove(index)}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
